'use client'

import Link from 'next/link'
import { PRICING_TIERS, TIER_LABELS, TIER_INTROS } from '../_content/tour-slides'

// Landing pricing — reuses the tier data from the shared tour source
// but renders in a landing-friendly visual (cards clickable, regular
// price as headline, alumni price as secondary line). No animation
// slider — numbers just appear (removes tour-native theatrics).
export function LandingPricing() {
  return (
    <section id="pricing" className="px-6 py-24 sm:py-32 bg-[#faf9f6]">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12 max-w-2xl mx-auto">
          <p className="text-[11px] uppercase tracking-[0.22em] text-bs-secondary-700 font-display font-semibold mb-4">
            Membership
          </p>
          <h2 className="font-display font-bold text-4xl sm:text-5xl leading-[1.05] tracking-tight text-gray-900 mb-4">
            Choose your{' '}
            <span className="italic text-bs-secondary-500">membership</span>.
          </h2>
          <p className="text-[16px] text-gray-600 leading-relaxed">
            Every membership includes community, referrals, tools, practice
            support, and clinical development. Only supervision varies by
            tier — pick the level of supervision you want.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PRICING_TIERS.map(tier => {
            const oneLiner = TIER_INTROS.find(t => t.tier === tier.tier)?.oneLiner
            return (
              <Link
                key={tier.tier}
                href={`/between-sessions/tour/join?tier=${tier.tier}`}
                className={
                  'group rounded-2xl bg-white p-6 flex flex-col hover:-translate-y-1 transition-all duration-300 relative cursor-pointer ' +
                  (tier.popular
                    ? 'border-2 border-bs-secondary-500 shadow-[0_16px_36px_-16px_rgba(108,95,199,0.35)] hover:shadow-[0_24px_44px_-16px_rgba(108,95,199,0.45)]'
                    : 'border border-[#ececec] hover:border-bs-secondary-200 hover:shadow-[0_20px_36px_-18px_rgba(20,20,20,0.2)]')
                }
              >
                {tier.popular && (
                  <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 inline-flex items-center px-2.5 py-0.5 rounded-full bg-bs-secondary-500 text-white text-[9px] font-display font-semibold uppercase tracking-[0.2em] shadow-[0_4px_10px_-4px_rgba(108,95,199,0.5)]">
                    Popular
                  </div>
                )}

                <div className="text-[10px] uppercase tracking-[0.22em] text-gray-500 font-display font-semibold mb-4">
                  {TIER_LABELS[tier.tier]}
                </div>

                <div className="flex items-baseline mb-2 whitespace-nowrap">
                  <span className="text-base font-display font-bold text-gray-700 mr-0.5">
                    $
                  </span>
                  <span className="text-3xl sm:text-4xl font-display font-bold text-gray-900 tabular-nums leading-none">
                    {tier.price}
                  </span>
                  <span className="text-xs font-display font-semibold text-gray-500 ml-1">
                    /mo
                  </span>
                </div>

                <p className="text-[11px] text-bs-secondary-600 font-display font-semibold mb-4">
                  {tier.alumniOrStudent}
                </p>

                <p className="text-[13px] text-gray-800 font-display font-medium leading-snug mb-5 mt-2">
                  {oneLiner}
                </p>

                <div className="mt-auto pt-4 border-t border-[#f0efea] flex items-center justify-between">
                  <span className="text-[11px] text-gray-600 font-display font-medium">
                    {tier.commitment}
                  </span>
                  <span className="text-[11px] text-bs-secondary-600 font-display font-semibold group-hover:text-gray-900 transition-colors">
                    Choose →
                  </span>
                </div>
              </Link>
            )
          })}
        </div>

        <ul className="max-w-2xl mx-auto mt-10 space-y-2.5">
          <li className="flex items-start gap-3">
            <span className="flex-shrink-0 mt-2 w-1.5 h-1.5 rounded-full bg-bs-secondary-500" />
            <span className="text-[13px] text-gray-700 leading-relaxed">
              <span className="font-display font-semibold text-gray-900">
                Add à la carte supervision to any tier
              </span>{' '}
              — individual $185, dyad $115, group $55 per credit. Packs of 4
              or 8 save more.
            </span>
          </li>
          <li className="flex items-start gap-3">
            <span className="flex-shrink-0 mt-2 w-1.5 h-1.5 rounded-full bg-bs-secondary-500" />
            <span className="text-[13px] text-gray-700 leading-relaxed">
              <span className="font-display font-semibold text-gray-900">
                Two commitment styles
              </span>{' '}
              — Core is month-to-month (start or cancel anytime). Community,
              Complete, and Complete+ run on 4-month cohorts (Winter, Spring,
              Fall) — switch at cohort boundaries.
            </span>
          </li>
        </ul>

        <p className="mt-4 text-center text-[11px] text-gray-500">
          All prices CAD, charged monthly.
        </p>
      </div>
    </section>
  )
}
